import { SHEET_NAME as OTHER_STAKEHOLDER_GROUP_SHEET } from "./otherStakeholderGroupSheet";

const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FF33CCFF" },
};

const SUBHEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFBEBEBE" },
};

const TABLE_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFCE4D6" },
};

/** Light grey band on roman section titles (II., Products/services, etc.) */
const SUBSECTION_TITLE_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { theme: 0, tint: -0.249977111117893 },
};

/** Peach-tint band on workforce A./B. subsection titles */
const WORKFORCE_SECTION_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { theme: 4, tint: 0.7999816888943144 },
};

/** Slightly darker grey on EMPLOYEES / WORKERS group banners */
const GROUP_BANNER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { theme: 0, tint: -0.1499984740745262 },
};

const GREY_FILL = SUBHEADER_FILL;

const THIN = { style: "thin", color: { argb: "FF000000" } };
const MEDIUM = { style: "medium", color: { argb: "FF000000" } };

const THIN_BLACK = {
  top: THIN,
  left: THIN,
  bottom: THIN,
  right: THIN,
};

const VALUE_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFFFFFF" },
};

const NA_REASON_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD9D9D9" },
};

const BODY_FONT = { name: "Calibri", size: 11 };
const LINK_FONT = {
  name: "Calibri",
  size: 11,
  color: { argb: "FF0563C1" },
  underline: true,
};

/** Table spans columns D (4) through L (12); simple values use F–I */
const COL_NUM = 4;
const COL_LABEL = 5;
const COL_VALUE_START = 6;
const COL_VALUE_END = 9;
const COL_LAST = 12;
const COL_SECTION_END = 17;

/** Location table columns within F–H */
const COL_LOC_PLANTS = 6;
const COL_LOC_OFFICES = 7;
const COL_LOC_TOTAL = 8;

/** Simple label merges end at column H; value bands through I */
const COL_H = 8;

/** FY date splits: start F–G, end H–I */
const COL_FY_START_END = 7;
const COL_FY_END_START = 8;

/** Main cyan title on row 4; rows 1–5 stay frozen (see sheet view). */
const SECTION_HEADER_ROW = 4;
const FREEZE_Y_SPLIT = 5;

const safeText = (value, fallback = "") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (Array.isArray(value)) return value.length ? value.join(", ") : fallback;
  if (typeof value === "object") return fallback;
  return String(value);
};

const safeNum = (value, fallback = "0") => {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toFixed(2);
};

const formatPct = (value) => {
  if (value === null || value === undefined || value === "") return "0.00";
  const text = String(value);
  return text.includes("%") ? text : `${text}%`;
};

function parsePct(value) {
  if (value === null || value === undefined || value === "") return NaN;
  return Number(String(value).replace("%", "").trim());
}

function getTurnoverOtherPct(obj) {
  if (obj?.other !== null && obj?.other !== undefined && obj?.other !== "") {
    return formatPct(obj.other);
  }
  const total = parsePct(obj?.total);
  const male = parsePct(obj?.male);
  const female = parsePct(obj?.female);
  if (!Number.isNaN(total) && !Number.isNaN(male) && !Number.isNaN(female)) {
    const other = Math.max(0, total - male - female);
    return formatPct(other.toFixed(2));
  }
  return formatPct(0);
}

function getOtherGender(obj) {
  const explicitCount = obj?.othergender ?? obj?.otherGender ?? obj?.genderOther;
  const explicitPct = obj?.otherpercentage ?? obj?.otherPercentage ?? obj?.genderOtherpercentage;

  if (explicitCount !== null && explicitCount !== undefined && explicitCount !== "") {
    return {
      count: safeNum(explicitCount),
      pct: formatPct(explicitPct ?? 0),
    };
  }

  const total = Number(obj?.total);
  const male = Number(obj?.male);
  const female = Number(obj?.female);

  if (Number.isNaN(total) || total <= 0) {
    return { count: "0", pct: "0.00%" };
  }

  const other = Math.max(0, total - (Number.isNaN(male) ? 0 : male) - (Number.isNaN(female) ? 0 : female));
  const pct = ((other / total) * 100).toFixed(2);
  return { count: String(other), pct: `${pct}%` };
}

const formatIndianDate = (day, month, year) => {
  const dd = String(day).padStart(2, "0");
  const mm = String(month).padStart(2, "0");
  return `${dd}-${mm}-${year}`;
};

const getFinancialYearRanges = (financialYear) => {
  const year = Number(financialYear);
  if (!year) {
    return {
      current: { label: "Current Financial Year", start: "", end: "" },
      previous: { label: "Previous Financial Year", start: "", end: "" },
      prior: { label: "Prior to Previous Financial year", start: "", end: "" },
    };
  }

  return {
    current: {
      label: "Current Financial Year",
      start: formatIndianDate(1, 4, year - 1),
      end: formatIndianDate(31, 3, year),
    },
    previous: {
      label: "Previous Financial Year",
      start: formatIndianDate(1, 4, year - 2),
      end: formatIndianDate(31, 3, year - 1),
    },
    prior: {
      label: "Prior to Previous Financial year",
      start: formatIndianDate(1, 4, year - 3),
      end: formatIndianDate(31, 3, year - 2),
    },
  };
};

function applyRowFill(ws, row, fill, startCol = COL_NUM, endCol = COL_SECTION_END) {
  for (let c = startCol; c <= endCol; c += 1) {
    ws.getCell(row, c).fill = fill;
  }
}

function borderCell(cell) {
  cell.border = THIN_BLACK;
}

function borderDataLabelCell(cell, { top = true } = {}) {
  cell.border = { left: THIN, bottom: THIN, ...(top ? { top: THIN } : {}) };
}

function borderDataValueCell(cell, { withFill = true, top = true } = {}) {
  cell.border = {
    left: THIN,
    right: THIN,
    bottom: THIN,
    ...(top ? { top: THIN } : {}),
  };
  if (withFill) cell.fill = VALUE_FILL;
}

function applyValueColumnFill(ws, row, col, fill = VALUE_FILL) {
  if (fill) ws.getCell(row, col).fill = fill;
}

/** Apply borders to each cell before merging (Excel shows only edge borders on merges). */
function applyMergedRangeBorders(ws, row, startCol, endCol, { top = true, bottom = true } = {}) {
  for (let c = startCol; c <= endCol; c += 1) {
    const border = {};
    if (top) border.top = THIN;
    if (bottom) border.bottom = THIN;
    if (c === startCol) border.left = THIN;
    if (c === endCol) border.right = THIN;
    ws.getCell(row, c).border = border;
  }
}

/** Label row merged E through endCol with right border on the last column (often H or I). */
function borderMergedLabelBandRow(ws, row, mergeEnd, { includeTopOnNum = true, bottom = THIN } = {}) {
  ws.getCell(row, COL_NUM).border = {
    ...(includeTopOnNum ? { top: THIN } : {}),
    left: MEDIUM,
    right: THIN,
    bottom,
  };
  ws.getCell(row, COL_LABEL).border = { top: THIN, left: THIN, bottom };
  for (let c = COL_VALUE_START; c < mergeEnd; c += 1) {
    ws.getCell(row, c).border = { top: THIN, bottom };
  }
  ws.getCell(row, mergeEnd).border = { top: THIN, right: THIN, bottom };
}

/** Sub-label row: label in E, value band F–I with right border on I. */
function borderSubLabelValueBandRow(ws, row) {
  ws.getCell(row, COL_NUM).border = { top: THIN, left: MEDIUM, right: THIN };
  ws.getCell(row, COL_LABEL).border = { top: THIN, left: THIN, right: THIN };
  ws.getCell(row, COL_VALUE_START).border = { top: THIN, left: THIN, bottom: THIN };
  ws.getCell(row, 7).border = { top: THIN, bottom: THIN };
  ws.getCell(row, COL_H).border = { top: THIN, bottom: THIN };
  ws.getCell(row, COL_VALUE_END).border = { top: THIN, right: THIN, bottom: THIN };
}

/** Full-width label through column H (locations title, etc.). */
function borderFullWidthLabelToH(ws, row) {
  ws.getCell(row, COL_NUM).border = { top: THIN, left: MEDIUM, bottom: THIN };
  ws.getCell(row, COL_LABEL).border = { top: THIN, left: THIN, bottom: THIN };
  ws.getCell(row, COL_LOC_PLANTS).border = { top: THIN, bottom: THIN };
  ws.getCell(row, COL_LOC_OFFICES).border = { top: THIN, bottom: THIN };
  ws.getCell(row, COL_H).border = { top: THIN, right: THIN, bottom: THIN };
}

function borderDataNumCell(cell, { top = false, bottom = true } = {}) {
  cell.border = {
    left: MEDIUM,
    right: THIN,
    ...(top ? { top: THIN } : {}),
    ...(bottom ? { bottom: THIN } : {}),
  };
}

function restoreSectionHeaderBorders(ws, row) {
  ws.getCell(row, COL_NUM).border = { top: MEDIUM, left: MEDIUM, bottom: MEDIUM };
  for (let c = COL_LABEL; c < COL_SECTION_END; c += 1) {
    ws.getCell(row, c).border = { top: MEDIUM, bottom: MEDIUM };
  }
  ws.getCell(row, COL_SECTION_END).border = { top: MEDIUM, right: MEDIUM, bottom: MEDIUM };
}

function borderLocationHeaderRow(ws, row) {
  ws.getCell(row, COL_NUM).border = { left: MEDIUM, bottom: THIN };
  for (let c = COL_LABEL; c <= COL_LOC_TOTAL; c += 1) {
    const border = { left: THIN, top: THIN, bottom: THIN };
    if (c === COL_LOC_TOTAL) border.right = THIN;
    const cell = ws.getCell(row, c);
    cell.border = border;
    cell.fill = TABLE_HEADER_FILL;
  }
}

function borderLocationDataRow(ws, row, { includeLabelTop = false } = {}) {
  ws.getCell(row, COL_NUM).border = { left: MEDIUM, bottom: THIN };
  const labelBorder = { left: THIN };
  if (includeLabelTop) labelBorder.top = THIN;
  ws.getCell(row, COL_LABEL).border = labelBorder;
  [COL_LOC_PLANTS, COL_LOC_OFFICES, COL_LOC_TOTAL].forEach((col) => {
    ws.getCell(row, col).border = THIN_BLACK;
  });
}

function borderMarketsTitleRow(ws, row, { numValue = "" } = {}) {
  const numCell = ws.getCell(row, COL_NUM);
  numCell.border = { left: MEDIUM, top: THIN, bottom: THIN };
  if (numValue) numCell.alignment = { horizontal: "right", vertical: "middle" };

  ws.mergeCells(row, COL_LABEL, row, COL_LABEL + 1);
  ws.getCell(row, COL_LABEL).border = { left: THIN, top: THIN, bottom: THIN, right: THIN };
}

function borderMarketsSubHeaderRow(ws, row) {
  ws.getCell(row, COL_NUM).border = { left: MEDIUM, top: THIN };
  const labelCell = ws.getCell(row, COL_LABEL);
  labelCell.border = { left: THIN, top: THIN, bottom: THIN };
  labelCell.fill = TABLE_HEADER_FILL;
  const numberCell = ws.getCell(row, COL_VALUE_START);
  numberCell.border = { left: THIN, top: THIN, bottom: THIN, right: THIN };
  numberCell.fill = TABLE_HEADER_FILL;
}

function workforceValueFill(col, { isTotal = false } = {}) {
  if (col === 6) return SUBSECTION_TITLE_FILL;
  if (isTotal && col >= 7) return GREY_FILL;
  if ([8, 10, 12].includes(col)) return GREY_FILL;
  if ([7, 9, 11].includes(col)) return VALUE_FILL;
  return null;
}

function womenRepValueFill(col) {
  if (col === 8) return GREY_FILL;
  if (col === 6 || col === 7) return VALUE_FILL;
  return null;
}

function needsTableRightBorder(col, tableStyle = "workforce") {
  if (col === COL_LABEL) return true;
  switch (tableStyle) {
    case "turnover":
      return [6, 9, 10, 13, 14].includes(col);
    case "complaints":
      if (col >= 6 && col <= 12) return true;
      return col === 14;
    default:
      return [6, 8, 10, COL_LAST].includes(col);
  }
}

function borderWorkforceHeaderRow1(ws, row) {
  ws.getCell(row, COL_NUM).border = { top: THIN, left: MEDIUM, right: THIN };
  ws.getCell(row, COL_LABEL).border = { left: THIN, right: THIN };
  ws.getCell(row, 6).border = { left: THIN, right: THIN };
  [7, 9, 11].forEach((col) => {
    ws.getCell(row, col).border = { left: THIN, right: THIN, bottom: THIN };
  });
}

function borderWorkforceHeaderRow2(ws, row) {
  ws.getCell(row, COL_NUM).border = { left: MEDIUM, right: THIN, bottom: THIN };
  ws.getCell(row, COL_LABEL).border = { left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 6).border = { left: THIN, right: THIN, bottom: THIN };
  for (let c = 7; c <= 12; c += 1) {
    ws.getCell(row, c).border = THIN_BLACK;
  }
}

function borderWorkforceBannerRow(ws, row) {
  ws.getCell(row, COL_NUM).border = { top: THIN, left: MEDIUM, bottom: THIN };
  ws.getCell(row, COL_LABEL).border = { top: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 6).border = { top: THIN, left: THIN, bottom: THIN };
  for (let c = 7; c <= 11; c += 1) {
    ws.getCell(row, c).border = { top: THIN, bottom: THIN };
  }
  ws.getCell(row, 12).border = { top: THIN, right: THIN, bottom: THIN };
}

function borderWorkforceSectionTitleRow(ws, row) {
  ws.getCell(row, COL_NUM).border = { left: MEDIUM, bottom: THIN };
  for (let c = COL_LABEL; c <= EMP_COL_END; c += 1) {
    const border = { top: THIN, bottom: THIN };
    if (c === COL_LABEL) border.left = THIN;
    if (c === EMP_COL_END) border.right = THIN;
    ws.getCell(row, c).border = border;
  }
}

function borderWorkforceDataCell(ws, row, col, { isTotal = false } = {}) {
  const cell = ws.getCell(row, col);
  if (col === COL_NUM) {
    cell.border = { top: THIN, left: MEDIUM, right: THIN, bottom: THIN };
  } else if (col === COL_LABEL) {
    cell.border = { top: THIN, left: THIN, bottom: THIN };
  } else if (col === 6) {
    cell.border = THIN_BLACK;
  } else if ([7, 9, 11].includes(col)) {
    cell.border = isTotal ? THIN_BLACK : { top: THIN };
  } else if ([8, 10, 12].includes(col)) {
    cell.border = THIN_BLACK;
  }
}

function applyWorkforceHeaderFill(ws, row, startCol = COL_NUM, endCol = EMP_COL_END) {
  for (let c = startCol; c <= endCol; c += 1) {
    ws.getCell(row, c).fill = TABLE_HEADER_FILL;
  }
}

function borderTableNumCell(cell, { top = false, bottom = true } = {}) {
  cell.border = {
    left: MEDIUM,
    right: THIN,
    ...(top ? { top: THIN } : {}),
    ...(bottom ? { bottom: THIN } : {}),
  };
}

function applySheetOuterBox(ws, startRow, endRow) {
  for (let r = startRow; r <= endRow; r += 1) {
    const dCell = ws.getCell(r, COL_NUM);
    const qCell = ws.getCell(r, COL_SECTION_END);
    const dBorder = { ...(dCell.border || {}), left: MEDIUM };
    const qBorder = { ...(qCell.border || {}), right: MEDIUM };

    if (r === startRow) {
      dBorder.top = MEDIUM;
      qBorder.top = MEDIUM;
    }
    if (r === endRow) {
      dBorder.bottom = MEDIUM;
      qBorder.bottom = MEDIUM;
    }

    dCell.border = dBorder;
    qCell.border = qBorder;
  }
}

function formatFyShort(endYear) {
  const fy = Number(endYear);
  if (!fy) return "";
  return `${fy - 1}-${String(fy).slice(-2)}`;
}

function applyMediumBottomTail(ws, row, fromCol = COL_VALUE_END + 1, toCol = COL_SECTION_END) {
  for (let c = fromCol; c <= toCol; c += 1) {
    const border = { bottom: MEDIUM };
    if (c === toCol) border.right = MEDIUM;
    ws.getCell(row, c).border = border;
  }
}

/** Grey fill for empty cells after the value / link cell (G→Q in ref). */
function applyTrailingBannerFill(ws, row, fromCol = COL_VALUE_START + 1, toCol = COL_SECTION_END) {
  for (let c = fromCol; c <= toCol; c += 1) {
    ws.getCell(row, c).fill = GROUP_BANNER_FILL;
  }
}

function borderLabelToHValueRow(ws, row, { sectionEnd = false } = {}) {
  const bottom = sectionEnd ? MEDIUM : THIN;

  ws.getCell(row, COL_NUM).border = {
    top: THIN,
    left: MEDIUM,
    right: THIN,
    bottom,
  };
  ws.getCell(row, COL_LABEL).border = { top: THIN, left: THIN, bottom };
  for (let c = COL_LABEL + 1; c < COL_H; c += 1) {
    ws.getCell(row, c).border = { left: THIN, bottom };
  }
  ws.getCell(row, COL_H).border = { top: THIN, left: THIN, right: THIN, bottom };
  ws.getCell(row, COL_VALUE_END).border = {
    top: THIN,
    left: THIN,
    right: THIN,
    bottom,
  };

  if (sectionEnd) {
    for (let c = COL_VALUE_END + 1; c <= COL_SECTION_END; c += 1) {
      const border = { bottom: MEDIUM };
      if (c === COL_SECTION_END) border.right = MEDIUM;
      ws.getCell(row, c).border = border;
    }
  }
}

function borderDetailLinkRow(ws, row, { labelMergeEnd = null, linkMergeEnd = null, sectionEnd = false } = {}) {
  const linkCol = labelMergeEnd ? labelMergeEnd + 1 : COL_VALUE_START;

  if (linkMergeEnd) {
    ws.getCell(row, COL_NUM).border = { top: THIN, left: MEDIUM, right: THIN, bottom: THIN };
    ws.getCell(row, COL_LABEL).border = THIN_BLACK;
    ws.getCell(row, COL_VALUE_START).border = { top: THIN, left: THIN, bottom: THIN };
    for (let c = COL_VALUE_START + 1; c < linkMergeEnd; c += 1) {
      ws.getCell(row, c).border = { top: THIN, bottom: THIN };
    }
    ws.getCell(row, linkMergeEnd).border = { top: THIN, right: THIN, bottom: THIN };
    ws.mergeCells(row, COL_VALUE_START, row, linkMergeEnd);
    return;
  }

  if (labelMergeEnd) {
    ws.getCell(row, COL_NUM).border = sectionEnd
      ? { top: THIN, left: MEDIUM, right: THIN }
      : { top: THIN, left: MEDIUM, right: THIN, bottom: THIN };

    if (sectionEnd) {
      ws.getCell(row, COL_LABEL).border = { top: THIN, right: THIN, bottom: MEDIUM };
      for (let c = COL_LABEL + 1; c <= labelMergeEnd; c += 1) {
        ws.getCell(row, c).border = { top: THIN, left: THIN, right: THIN, bottom: MEDIUM };
      }
      ws.getCell(row, linkCol).border = {
        top: THIN,
        left: THIN,
        right: THIN,
        bottom: MEDIUM,
      };
      for (let c = linkCol + 1; c <= COL_SECTION_END; c += 1) {
        const border = { bottom: MEDIUM };
        if (c === COL_SECTION_END) border.right = MEDIUM;
        ws.getCell(row, c).border = border;
      }
    } else {
      ws.getCell(row, COL_LABEL).border = { right: THIN, bottom: THIN };
      for (let c = COL_LABEL + 1; c <= labelMergeEnd; c += 1) {
        ws.getCell(row, c).border = { left: THIN, right: THIN, bottom: THIN };
      }
      ws.getCell(row, linkCol).border = {
        top: THIN,
        left: THIN,
        right: THIN,
        bottom: THIN,
      };
    }

    ws.mergeCells(row, COL_LABEL, row, labelMergeEnd);
    ws.getCell(row, labelMergeEnd).border = {
      ...(sectionEnd ? { top: THIN } : {}),
      left: THIN,
      right: THIN,
      bottom: sectionEnd ? MEDIUM : THIN,
    };
    return;
  }

  if (sectionEnd) {
    ws.getCell(row, COL_NUM).border = { top: THIN, left: MEDIUM, right: THIN, bottom: MEDIUM };
    ws.getCell(row, COL_LABEL).border = { top: THIN, left: THIN, right: THIN, bottom: MEDIUM };
    ws.getCell(row, linkCol).border = { top: THIN, left: THIN, right: THIN, bottom: MEDIUM };
    for (let c = linkCol + 1; c <= COL_SECTION_END; c += 1) {
      ws.getCell(row, c).border = {};
    }
    return;
  }

  ws.getCell(row, COL_NUM).border = { top: THIN, left: MEDIUM, right: THIN, bottom: THIN };
  ws.getCell(row, COL_LABEL).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, linkCol).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
}

function borderExtendedTitleRow(ws, row, endCol = COL_SECTION_END) {
  ws.getCell(row, COL_NUM).border = { left: MEDIUM, bottom: THIN };
  ws.getCell(row, COL_LABEL).border = { left: THIN, bottom: THIN };
  for (let c = COL_VALUE_START; c <= endCol; c += 1) {
    const border = { bottom: THIN };
    if (c !== 8) border.top = THIN;
    if (c === COL_VALUE_START) border.left = THIN;
    if (c === endCol) border.right = MEDIUM;
    ws.getCell(row, c).border = border;
  }
}

function borderTurnoverHeaderRow1(ws, row) {
  ws.getCell(row, COL_NUM).border = { top: THIN, left: MEDIUM, right: THIN };
  ws.getCell(row, COL_LABEL).border = { top: THIN, right: THIN };
  const periodBorders = [
    [6, { top: THIN, left: THIN, bottom: THIN }],
    [7, { top: THIN, bottom: THIN }],
    [8, { top: THIN, bottom: THIN }],
    [9, { top: THIN, right: THIN, bottom: THIN }],
    [10, { top: THIN, left: THIN, bottom: THIN }],
    [11, { top: THIN, bottom: THIN }],
    [12, { top: THIN, bottom: THIN }],
    [13, { top: THIN, right: THIN, bottom: THIN }],
    [14, { top: THIN, left: THIN, bottom: THIN }],
    [15, { top: THIN, bottom: THIN }],
    [16, { top: THIN, bottom: THIN }],
    [17, { top: THIN, right: MEDIUM, bottom: THIN }],
  ];
  periodBorders.forEach(([col, border]) => {
    ws.getCell(row, col).border = border;
  });
}

function borderTurnoverHeaderRow2(ws, row) {
  ws.getCell(row, COL_NUM).border = { left: MEDIUM, right: THIN, bottom: THIN };
  ws.getCell(row, COL_LABEL).border = { right: THIN, bottom: THIN };
  const cols = [
    [6, THIN_BLACK],
    [7, { top: THIN, left: THIN, bottom: THIN }],
    [8, { top: THIN, left: THIN, bottom: THIN }],
    [9, { top: THIN, left: THIN, right: THIN, bottom: THIN }],
    [10, { top: THIN, right: THIN, bottom: THIN }],
    [11, { top: THIN, left: THIN, bottom: THIN }],
    [12, { top: THIN, left: THIN, bottom: THIN }],
    [13, { top: THIN, left: THIN, right: THIN, bottom: THIN }],
    [14, { top: THIN, left: THIN, right: THIN, bottom: THIN }],
    [15, { top: THIN, left: THIN, bottom: THIN }],
    [16, { top: THIN, left: THIN, bottom: THIN }],
    [17, { top: THIN, left: THIN, right: MEDIUM, bottom: THIN }],
  ];
  cols.forEach(([col, border]) => {
    ws.getCell(row, col).border = border;
  });
}

function borderTurnoverDataRow(ws, row, { lastRow = false } = {}) {
  const bottom = lastRow ? MEDIUM : THIN;
  ws.getCell(row, COL_NUM).border = {
    left: MEDIUM,
    right: THIN,
    ...(lastRow ? { bottom: MEDIUM } : {}),
  };
  ws.getCell(row, COL_LABEL).border = { top: THIN, right: THIN, bottom };
  const valueCols = [
    [6, THIN_BLACK],
    [7, THIN_BLACK],
    [8, { top: THIN, left: THIN, bottom }],
    [9, { top: THIN, left: THIN }],
    [10, THIN_BLACK],
    [11, THIN_BLACK],
    [12, { top: THIN, left: THIN, bottom }],
    [13, { top: THIN, left: THIN, right: THIN, bottom }],
    [14, THIN_BLACK],
    [15, THIN_BLACK],
    [16, { top: THIN, left: THIN, bottom }],
    [17, { top: THIN, left: THIN, right: MEDIUM, ...(lastRow ? { bottom: MEDIUM } : {}) }],
  ];
  valueCols.forEach(([col, border]) => {
    const resolved = border === THIN_BLACK
      ? { top: THIN, left: THIN, right: THIN, bottom }
      : { ...border, bottom: border.bottom || bottom };
    ws.getCell(row, col).border = resolved;
  });
}

function addSubPointRow(ws, row, number, label, value, { suppressNumTop = false, sectionEnd = false, valueBandEnd = false } = {}) {
  const numCell = ws.getCell(row, COL_NUM);
  numCell.value = number || "";
  numCell.font = BODY_FONT;
  if (number) borderDataNumCell(numCell, { top: !suppressNumTop });
  else if (valueBandEnd) numCell.border = { left: MEDIUM };
  else borderTableNumCell(numCell, { top: true });
  numCell.alignment = { horizontal: "right", vertical: "middle" };

  setLabelCell(ws, row, label, false, { top: !valueBandEnd });
  setValueCell(ws, row, value, { mergeEnd: COL_VALUE_START, align: "left" });

  if (valueBandEnd) {
    ws.getCell(row, COL_LABEL).border = { left: THIN, right: THIN };
    ws.getCell(row, COL_VALUE_START).border = THIN_BLACK;
  }

  if (sectionEnd) {
    ws.getCell(row, COL_NUM).border = {
      top: THIN,
      left: MEDIUM,
      right: THIN,
      bottom: MEDIUM,
    };
    ws.getCell(row, COL_LABEL).border = {
      top: THIN,
      left: THIN,
      right: THIN,
      bottom: MEDIUM,
    };
    ws.getCell(row, COL_VALUE_START).border = {
      top: THIN,
      left: THIN,
      right: THIN,
      bottom: MEDIUM,
    };
    for (let c = COL_VALUE_START + 1; c <= COL_SECTION_END; c += 1) {
      const border = { bottom: MEDIUM };
      if (c === COL_SECTION_END) border.right = MEDIUM;
      ws.getCell(row, c).border = border;
    }
  }

  applyTrailingBannerFill(ws, row);
  return row + 1;
}

function styleSectionHeader(ws, row, title) {
  for (let c = COL_NUM; c <= COL_SECTION_END; c += 1) {
    const cell = ws.getCell(row, c);
    cell.fill = HEADER_FILL;
    if (c === COL_NUM) {
      cell.border = { top: MEDIUM, left: MEDIUM, bottom: MEDIUM };
    } else if (c === COL_SECTION_END) {
      cell.border = { top: MEDIUM, right: MEDIUM, bottom: MEDIUM };
    } else {
      cell.border = { top: MEDIUM, bottom: MEDIUM };
    }
  }

  ws.mergeCells(row, COL_NUM, row, COL_SECTION_END);
  const cell = ws.getCell(row, COL_NUM);
  cell.value = title;
  cell.font = { name: "Calibri", size: 14, bold: true };
  cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  ws.getRow(row).height = 30;
}

function styleSubSectionHeader(ws, row, roman, title, variant = "divider") {
  const romanCell = ws.getCell(row, COL_NUM);
  romanCell.value = roman;
  romanCell.font = { name: "Calibri", size: 11, bold: true };
  romanCell.alignment = { horizontal: "center", vertical: "middle" };

  const titleCell = ws.getCell(row, COL_LABEL);
  titleCell.value = title;
  titleCell.font = { name: "Calibri", size: 14, bold: true };
  titleCell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };

  if (variant === "greyFull") {
    romanCell.fill = SUBHEADER_FILL;
    romanCell.border = { left: MEDIUM, right: THIN, bottom: THIN };
    titleCell.fill = SUBHEADER_FILL;
    titleCell.border = { bottom: THIN };
    for (let col = COL_LABEL + 1; col <= COL_SECTION_END; col += 1) {
      const cell = ws.getCell(row, col);
      cell.fill = SUBHEADER_FILL;
      cell.border = { bottom: THIN };
    }
    return;
  }

  const topOnly = { top: MEDIUM, bottom: THIN };
  if (variant === "greyRoman") {
    romanCell.fill = SUBHEADER_FILL;
    romanCell.border = { left: MEDIUM, ...topOnly };
    applyRowFill(ws, row, SUBSECTION_TITLE_FILL, COL_LABEL, COL_SECTION_END);
    titleCell.border = topOnly;
  } else {
    applyRowFill(ws, row, SUBSECTION_TITLE_FILL);
    romanCell.border = { left: MEDIUM, ...topOnly };
    titleCell.border = topOnly;
  }

  for (let col = COL_LABEL + 1; col <= COL_SECTION_END; col += 1) {
    ws.getCell(row, col).border = topOnly;
  }
}

function setLabelCell(ws, row, label, mandatory = false, { top = true } = {}) {
  const cell = ws.getCell(row, COL_LABEL);
  if (mandatory) {
    cell.value = {
      richText: [
        { text: label, font: { ...BODY_FONT, color: { argb: "FF000000" } } },
        { text: "*", font: { ...BODY_FONT, color: { argb: "FFFF0000" } } },
      ],
    };
  } else {
    cell.value = label;
    cell.font = BODY_FONT;
  }
  borderDataLabelCell(cell, { top });
  cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
}

function setValueCell(ws, row, value, options = {}) {
  const {
    link = null,
    align = "left",
    mergeStart = COL_VALUE_START,
    mergeEnd = COL_VALUE_END,
    withFill = true,
    top = true,
    merge = false,
  } = options;

  if (merge) {
    applyMergedRangeBorders(ws, row, mergeStart, mergeEnd, { top });
    ws.mergeCells(row, mergeStart, row, mergeEnd);
    ws.getCell(row, mergeEnd).border = {
      ...(ws.getCell(row, mergeEnd).border || {}),
      right: THIN,
    };
  } else {
    for (let c = mergeStart; c <= mergeEnd; c += 1) {
      const border = { bottom: THIN };
      if (top) border.top = THIN;
      if (c === mergeStart) border.left = THIN;
      if (c === mergeEnd) border.right = THIN;
      const cell = ws.getCell(row, c);
      cell.border = border;
      if (withFill && c === mergeStart) cell.fill = VALUE_FILL;
    }
  }

  const cell = ws.getCell(row, mergeStart);

  if (link) {
    const sheetName = link.replace(/^'|'$/g, "");
    cell.value = {
      text: value || "Add details",
      hyperlink: `#'${sheetName}'!D3`,
    };
    cell.font = LINK_FONT;
    cell.alignment = { horizontal: "center", vertical: "middle" };
  } else {
    cell.value = value;
    cell.font = BODY_FONT;
    cell.alignment = { horizontal: align, vertical: "middle", wrapText: true };
  }

  if (merge && withFill) cell.fill = VALUE_FILL;
}

function addLabelValueRow(ws, row, number, label, value, options = {}) {
  const {
    mandatory = false,
    link = null,
    valueAlign = "left",
    suppressNumTop = false,
    withFill = true,
    mergeValueBand = true,
    labelMergeEnd = null,
    sectionEnd = false,
  } = options;

  const numCell = ws.getCell(row, COL_NUM);
  numCell.value = number;
  numCell.font = BODY_FONT;
  borderDataNumCell(numCell, { top: !suppressNumTop });
  numCell.alignment = { horizontal: "right", vertical: "middle" };

  if (labelMergeEnd === COL_H) {
    const labelCell = ws.getCell(row, COL_LABEL);
    if (mandatory) {
      labelCell.value = {
        richText: [
          { text: label, font: { ...BODY_FONT, color: { argb: "FF000000" } } },
          { text: "*", font: { ...BODY_FONT, color: { argb: "FFFF0000" } } },
        ],
      };
    } else {
      labelCell.value = label;
      labelCell.font = BODY_FONT;
    }
    labelCell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };

    borderLabelToHValueRow(ws, row, { sectionEnd });
    ws.mergeCells(row, COL_LABEL, row, COL_H);

    const bottom = sectionEnd ? MEDIUM : THIN;
    ws.getCell(row, COL_H).border = { top: THIN, left: THIN, right: THIN, bottom };

    setValueCell(ws, row, value, {
      link,
      align: valueAlign,
      withFill,
      merge: false,
      mergeStart: COL_VALUE_END,
      mergeEnd: COL_VALUE_END,
    });
    ws.getCell(row, COL_VALUE_END).border = {
      top: THIN,
      left: THIN,
      right: THIN,
      bottom,
    };
    applyTrailingBannerFill(ws, row, COL_VALUE_END + 1);

    return row + 1;
  }

  setLabelCell(ws, row, label, mandatory);
  setValueCell(ws, row, value, {
    link,
    align: valueAlign,
    withFill,
    merge: mergeValueBand,
    mergeEnd: mergeValueBand ? COL_VALUE_END : COL_VALUE_START,
  });
  applyTrailingBannerFill(
    ws,
    row,
    mergeValueBand ? COL_VALUE_END + 1 : COL_VALUE_START + 1
  );

  return row + 1;
}

function addFullWidthLabelRow(ws, row, label, number = "", mergeEnd = COL_VALUE_END, options = {}) {
  const {
    font = BODY_FONT,
    bold = false,
    suppressNumTop = false,
    wideMerge = false,
    extendedTitle = false,
    sectionTitle = false,
  } = options;
  const useTitleFill = sectionTitle || extendedTitle || wideMerge;
  const titleBold = bold || sectionTitle || extendedTitle;
  const numCell = ws.getCell(row, COL_NUM);
  numCell.value = number;
  numCell.font = titleBold && number ? { ...BODY_FONT, bold: true } : BODY_FONT;
  if (number) borderDataNumCell(numCell, { top: !suppressNumTop });
  else borderTableNumCell(numCell, { top: !suppressNumTop });
  numCell.alignment = { horizontal: "right", vertical: "middle" };

  if (extendedTitle) {
    const labelCell = ws.getCell(row, COL_LABEL);
    labelCell.value = label;
    labelCell.font = titleBold ? { ...font, bold: true } : font;
    labelCell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
    borderExtendedTitleRow(ws, row, mergeEnd);
    if (useTitleFill) applyRowFill(ws, row, SUBSECTION_TITLE_FILL);
    if (mergeEnd < COL_SECTION_END) applyTrailingBannerFill(ws, row, mergeEnd + 1);
    return row + 1;
  }

  if (wideMerge) {
    ws.getCell(row, COL_NUM).border = { top: THIN, left: MEDIUM, right: THIN, bottom: THIN };
    for (let c = COL_LABEL; c <= mergeEnd; c += 1) {
      const border = { top: THIN };
      if (c === COL_LABEL) border.left = THIN;
      ws.getCell(row, c).border = border;
    }
    ws.mergeCells(row, COL_LABEL, row, mergeEnd);
    const cell = ws.getCell(row, COL_LABEL);
    cell.value = label;
    cell.font = titleBold ? { ...font, bold: true } : font;
    cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
    if (useTitleFill) applyRowFill(ws, row, SUBSECTION_TITLE_FILL);
    if (mergeEnd < COL_SECTION_END) applyTrailingBannerFill(ws, row, mergeEnd + 1);
    return row + 1;
  } else {
    ws.getCell(row, COL_LABEL).border = { left: THIN, bottom: THIN, top: THIN };
  }

  ws.mergeCells(row, COL_LABEL, row, mergeEnd);
  const cell = ws.getCell(row, COL_LABEL);
  cell.value = label;
  cell.font = titleBold ? { ...font, bold: true } : font;
  cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };

  if (mergeEnd === COL_H && !useTitleFill && !wideMerge && !extendedTitle) {
    borderFullWidthLabelToH(ws, row);
  } else if (!useTitleFill && !wideMerge && !extendedTitle) {
    borderMergedLabelBandRow(ws, row, mergeEnd);
  }

  if (useTitleFill) applyRowFill(ws, row, SUBSECTION_TITLE_FILL);
  if (mergeEnd < COL_SECTION_END) {
    const trailingFrom =
      useTitleFill && mergeEnd === COL_VALUE_END ? mergeEnd : mergeEnd + 1;
    applyTrailingBannerFill(ws, row, trailingFrom);
  }

  return row + 1;
}

function addDetailLinkRow(ws, row, number, label, sheetName, { labelMergeEnd = null, linkMergeEnd = null, sectionEnd = false, suppressNumTop = false } = {}) {
  const numCell = ws.getCell(row, COL_NUM);
  numCell.value = number;
  numCell.font = BODY_FONT;
  borderDataNumCell(numCell, { top: !suppressNumTop });
  numCell.alignment = { horizontal: "right", vertical: "middle" };

  const linkCol = linkMergeEnd ? COL_VALUE_START : (labelMergeEnd ? labelMergeEnd + 1 : COL_VALUE_START);
  const labelCell = ws.getCell(row, COL_LABEL);
  labelCell.value = label;
  labelCell.font = BODY_FONT;
  labelCell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };

  const linkCell = ws.getCell(row, linkCol);
  const cleanName = sheetName.replace(/^'|'$/g, "");
  linkCell.value = {
    text: "Add details",
    hyperlink: `#'${cleanName}'!D3`,
  };
  linkCell.font = LINK_FONT;
  applyValueColumnFill(ws, row, linkCol);
  linkCell.alignment = { horizontal: "center", vertical: "middle" };

  borderDetailLinkRow(ws, row, { labelMergeEnd, linkMergeEnd, sectionEnd });

  if (linkMergeEnd) {
    ws.getCell(row, linkMergeEnd).border = { top: THIN, right: THIN, bottom: THIN };
    applyTrailingBannerFill(ws, row, linkMergeEnd + 1);
  } else if (labelMergeEnd) {
    ws.getCell(row, linkCol).border = {
      top: THIN,
      left: THIN,
      right: THIN,
      bottom: sectionEnd ? MEDIUM : THIN,
    };
    applyTrailingBannerFill(ws, row, linkCol + 1);
  } else if (!sectionEnd) {
    ws.getCell(row, linkCol).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
    applyTrailingBannerFill(ws, row, linkCol + 1);
  } else {
    applyTrailingBannerFill(ws, row, linkCol + 1);
  }

  return row + 1;
}

function addSubLabelValueRow(ws, row, label, value) {
  borderTableNumCell(ws.getCell(row, COL_NUM), { top: true });
  setLabelCell(ws, row, label);
  setValueCell(ws, row, value, { merge: true, mergeEnd: COL_VALUE_END });
  borderSubLabelValueBandRow(ws, row);
  applyTrailingBannerFill(ws, row, COL_VALUE_END + 1);
  return row + 1;
}

function addContactHeaderRow(ws, row, number, label, { suppressNumTop = false } = {}) {
  const numCell = ws.getCell(row, COL_NUM);
  numCell.value = number;
  numCell.font = BODY_FONT;
  borderDataNumCell(numCell, { top: !suppressNumTop });
  numCell.alignment = { horizontal: "right", vertical: "middle" };

  const labelCell = ws.getCell(row, COL_LABEL);
  labelCell.value = label;
  labelCell.font = BODY_FONT;
  labelCell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };

  borderMergedLabelBandRow(ws, row, COL_VALUE_END);
  ws.mergeCells(row, COL_LABEL, row, COL_VALUE_END);
  ws.getCell(row, COL_VALUE_END).border = { top: THIN, right: THIN, bottom: THIN };
  applyRowFill(ws, row, GROUP_BANNER_FILL, COL_LABEL, COL_VALUE_END);
  applyTrailingBannerFill(ws, row, COL_VALUE_END + 1);

  return row + 1;
}

function addFinancialYearBlock(ws, row, fyRanges) {
  const numCell = ws.getCell(row, COL_NUM);
  numCell.value = "13";
  numCell.font = BODY_FONT;
  borderDataNumCell(numCell, { top: true });
  numCell.alignment = { horizontal: "right", vertical: "middle" };

  setLabelCell(ws, row, "Financial year for which reporting is being done");

  for (let c = COL_VALUE_START; c <= COL_VALUE_END; c += 1) {
    ws.getCell(row, c).border = THIN_BLACK;
  }
  ws.mergeCells(row, COL_VALUE_START, row, COL_FY_START_END);
  const startHeader = ws.getCell(row, COL_VALUE_START);
  startHeader.value = "Start date";
  startHeader.font = BODY_FONT;
  startHeader.alignment = { horizontal: "center", vertical: "middle" };

  ws.mergeCells(row, COL_FY_END_START, row, COL_VALUE_END);
  const endHeader = ws.getCell(row, COL_FY_END_START);
  endHeader.value = "End date";
  endHeader.font = BODY_FONT;
  endHeader.alignment = { horizontal: "center", vertical: "middle" };
  applyRowFill(ws, row, GROUP_BANNER_FILL, COL_LABEL, COL_VALUE_END);
  applyTrailingBannerFill(ws, row, COL_VALUE_END + 1);
  row += 1;

  [fyRanges.current, fyRanges.previous, fyRanges.prior].forEach((fy) => {
    borderDataNumCell(ws.getCell(row, COL_NUM), { top: true });
    setLabelCell(ws, row, fy.label);

    for (let c = COL_VALUE_START; c <= COL_FY_START_END; c += 1) {
      const border = { top: THIN, bottom: THIN };
      if (c === COL_VALUE_START) border.left = THIN;
      if (c === COL_FY_START_END) border.right = THIN;
      ws.getCell(row, c).border = border;
    }
    applyValueColumnFill(ws, row, COL_VALUE_START);
    const startCell = ws.getCell(row, COL_VALUE_START);
    startCell.value = fy.start;
    startCell.font = BODY_FONT;
    startCell.alignment = { horizontal: "center", vertical: "middle" };

    for (let c = COL_FY_END_START; c <= COL_VALUE_END; c += 1) {
      const border = { top: THIN, bottom: THIN };
      if (c === COL_FY_END_START) border.left = THIN;
      if (c === COL_VALUE_END) border.right = THIN;
      ws.getCell(row, c).border = border;
    }
    applyValueColumnFill(ws, row, COL_FY_END_START);
    const endCell = ws.getCell(row, COL_FY_END_START);
    endCell.value = fy.end;
    endCell.font = BODY_FONT;
    endCell.alignment = { horizontal: "center", vertical: "middle" };

    applyTrailingBannerFill(ws, row, COL_VALUE_END + 1);
    row += 1;
  });

  return row;
}

/** Employee tables use columns E–L; turnover table extends to Q */
const EMP_COL_END = 12;
const TURNOVER_FY_COLS = 4;
const TURNOVER_TABLE_END = 17;

function setTableCell(ws, row, col, value, options = {}) {
  const cell = ws.getCell(row, col);
  cell.value = value;
  cell.font = options.bold ? { ...BODY_FONT, bold: true } : BODY_FONT;
  const tableStyle = options.tableStyle || "workforce";
  if (!options.skipBorder) {
    if (col === COL_NUM) {
      borderTableNumCell(cell, { top: options.top, bottom: options.bottom !== false });
    } else {
      const border = { bottom: THIN };
      if (options.left !== false) border.left = THIN;
      if (options.right !== false && (options.right || needsTableRightBorder(col, tableStyle))) {
        border.right = THIN;
      }
      if (options.top) border.top = THIN;
      cell.border = border;
    }
  }
  cell.alignment = {
    horizontal: options.align || "center",
    vertical: "middle",
    wrapText: true,
  };
  if (options.fill) cell.fill = options.fill;
  else if (options.headerFill) cell.fill = TABLE_HEADER_FILL;
  else if (options.naFill) cell.fill = NA_REASON_FILL;
  else if (options.workforceFill) {
    const fill = workforceValueFill(col, { isTotal: options.isTotal });
    if (fill) cell.fill = fill;
  } else if (options.womenFill) {
    const fill = womenRepValueFill(col);
    if (fill) cell.fill = fill;
  } else if (options.valueFill) applyValueColumnFill(ws, row, col);
}

function addWorkforceSectionTitle(ws, row, letter, label) {
  applyRowFill(ws, row, WORKFORCE_SECTION_FILL, COL_NUM, EMP_COL_END);

  const letterCell = ws.getCell(row, COL_NUM);
  letterCell.value = letter;
  letterCell.font = { ...BODY_FONT, bold: true };
  letterCell.alignment = { horizontal: "right", vertical: "middle" };

  const labelCell = ws.getCell(row, COL_LABEL);
  labelCell.value = label;
  labelCell.font = { ...BODY_FONT, bold: true };
  labelCell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };

  ws.mergeCells(row, COL_LABEL, row, EMP_COL_END);
  borderWorkforceSectionTitleRow(ws, row);
  applyTrailingBannerFill(ws, row, EMP_COL_END + 1);

  return row + 1;
}

function addWorkforceTableHeader(ws, row) {
  const r1 = row;
  const r2 = row + 1;

  setTableCell(ws, r1, COL_NUM, "Sr.\nNo.", { skipBorder: true });
  setTableCell(ws, r1, COL_LABEL, "Particulars", { align: "left", skipBorder: true });
  setTableCell(ws, r1, 6, "Total(A)", { skipBorder: true });
  setTableCell(ws, r1, 7, "Male", { skipBorder: true });
  setTableCell(ws, r1, 9, "Female", { skipBorder: true });
  setTableCell(ws, r1, 11, "Other", { skipBorder: true });
  setTableCell(ws, r2, 7, "No. (B)", { skipBorder: true });
  setTableCell(ws, r2, 8, "% (B / A)", { skipBorder: true });
  setTableCell(ws, r2, 9, "No. (C)", { skipBorder: true });
  setTableCell(ws, r2, 10, "% (C / A)", { skipBorder: true });
  setTableCell(ws, r2, 11, "No. (H)", { skipBorder: true });
  setTableCell(ws, r2, EMP_COL_END, "% (H / A)", { skipBorder: true });

  ws.mergeCells(r1, COL_NUM, r2, COL_NUM);
  ws.mergeCells(r1, COL_LABEL, r2, COL_LABEL);
  ws.mergeCells(r1, 6, r2, 6);
  ws.mergeCells(r1, 7, r1, 8);
  ws.mergeCells(r1, 9, r1, 10);
  ws.mergeCells(r1, 11, r1, EMP_COL_END);

  borderWorkforceHeaderRow1(ws, r1);
  borderWorkforceHeaderRow2(ws, r2);
  applyWorkforceHeaderFill(ws, r1);
  applyWorkforceHeaderFill(ws, r2);
  applyTrailingBannerFill(ws, r1, EMP_COL_END + 1);
  applyTrailingBannerFill(ws, r2, EMP_COL_END + 1);

  return r2 + 1;
}

function addWorkforceGroupBanner(ws, row, title) {
  applyRowFill(ws, row, GROUP_BANNER_FILL, COL_NUM, EMP_COL_END);

  const letterCell = ws.getCell(row, COL_NUM);
  letterCell.value = title;
  letterCell.font = { ...BODY_FONT, bold: true };
  letterCell.alignment = { horizontal: "center", vertical: "middle" };

  borderWorkforceBannerRow(ws, row);
  ws.mergeCells(row, COL_NUM, row, COL_LABEL);
  ws.getCell(row, COL_LABEL).border = {
    top: THIN,
    right: THIN,
    bottom: THIN,
  };
  applyTrailingBannerFill(ws, row, EMP_COL_END + 1);

  return row + 1;
}

function addWorkforceDataRow(ws, row, srNo, label, obj, { isTotal = false } = {}) {
  const other = getOtherGender(obj);

  setTableCell(ws, row, COL_NUM, String(srNo), { align: "center", skipBorder: true });
  setTableCell(ws, row, COL_LABEL, label, { align: "left", skipBorder: true });
  setTableCell(ws, row, 6, safeNum(obj?.total), { align: "right", workforceFill: true, isTotal, skipBorder: true });
  setTableCell(ws, row, 7, safeNum(obj?.male), { align: "right", workforceFill: true, isTotal, skipBorder: true });
  setTableCell(ws, row, 8, formatPct(obj?.malepercentage), { align: "right", workforceFill: true, isTotal, skipBorder: true });
  setTableCell(ws, row, 9, safeNum(obj?.female), { align: "right", workforceFill: true, isTotal, skipBorder: true });
  setTableCell(ws, row, 10, formatPct(obj?.femalepercentage), { align: "right", workforceFill: true, isTotal, skipBorder: true });
  setTableCell(ws, row, 11, other.count, { align: "right", workforceFill: true, isTotal, skipBorder: true });
  setTableCell(ws, row, EMP_COL_END, other.pct, { align: "right", workforceFill: true, isTotal, skipBorder: true });

  for (let c = COL_NUM; c <= EMP_COL_END; c += 1) {
    borderWorkforceDataCell(ws, row, c, { isTotal });
  }
  applyTrailingBannerFill(ws, row, EMP_COL_END + 1);

  return row + 1;
}

function addWorkforceGroup(ws, row, bannerTitle, node, rowDefs, startSr) {
  row = addWorkforceGroupBanner(ws, row, bannerTitle);
  let sr = startSr;

  rowDefs.forEach(({ label, key }) => {
    row = addWorkforceDataRow(ws, row, sr, label, node?.[key], { isTotal: key === "total" });
    sr += 1;
  });

  return { row, nextSr: sr };
}

function borderWomenRepHeaderRow1(ws, row) {
  ws.getCell(row, COL_NUM).border = { top: THIN, left: MEDIUM, right: THIN };
  ws.getCell(row, COL_LABEL).border = { top: THIN, left: THIN, right: THIN };
  ws.getCell(row, 6).border = { top: THIN, left: THIN, right: THIN };
  ws.getCell(row, 7).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
}

function borderWomenRepHeaderRow2(ws, row) {
  ws.getCell(row, COL_NUM).border = { left: MEDIUM, right: THIN, bottom: THIN };
  ws.getCell(row, COL_LABEL).border = { left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 6).border = { left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 7).border = { top: THIN, left: THIN, bottom: THIN };
  ws.getCell(row, 8).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
}

function borderWomenRepDataRow(ws, row) {
  ws.getCell(row, COL_NUM).border = { left: MEDIUM };
  ws.getCell(row, COL_LABEL).border = THIN_BLACK;
  ws.getCell(row, 6).border = { top: THIN, left: THIN, right: THIN };
  ws.getCell(row, 7).border = { top: THIN, left: THIN };
  ws.getCell(row, 8).border = THIN_BLACK;
}

function addWomenRepTable(ws, row, womenRep) {
  const r1 = row;
  const r2 = row + 1;

  setTableCell(ws, r1, 6, "Total (A)", { skipBorder: true });
  setTableCell(ws, r1, 7, "No. and percentage of Females", { skipBorder: true });
  setTableCell(ws, r2, 6, "Total (A)", { skipBorder: true });
  setTableCell(ws, r2, 7, "No. (B)", { skipBorder: true });
  setTableCell(ws, r2, 8, "% (B / A)", { skipBorder: true });

  ws.mergeCells(r1, COL_LABEL, r2, COL_LABEL);
  ws.mergeCells(r1, 6, r2, 6);
  ws.mergeCells(r1, 7, r1, 8);

  borderWomenRepHeaderRow1(ws, r1);
  borderWomenRepHeaderRow2(ws, r2);
  applyWorkforceHeaderFill(ws, r1, COL_NUM, 8);
  applyWorkforceHeaderFill(ws, r2, COL_NUM, 8);
  applyTrailingBannerFill(ws, r1, 9);
  applyTrailingBannerFill(ws, r2, 9);
  row += 2;

  const items = [
    { label: "Board of Directors", obj: womenRep?.boardOfDirectors },
    { label: "Key Management Personnel", obj: womenRep?.keyManagementPersonnel },
  ];

  items.forEach(({ label, obj }) => {
    setTableCell(ws, row, COL_LABEL, label, { align: "left", skipBorder: true });
    setTableCell(ws, row, 6, safeNum(obj?.total), { align: "right", womenFill: true, skipBorder: true });
    setTableCell(ws, row, 7, safeNum(obj?.female), { align: "right", womenFill: true, skipBorder: true });
    setTableCell(ws, row, 8, formatPct(obj?.femalepercentage), { align: "right", womenFill: true, skipBorder: true });
    borderWomenRepDataRow(ws, row);
    applyTrailingBannerFill(ws, row, 9);
    row += 1;
  });

  return row;
}

function addTableBanner(ws, row, title) {
  borderCell(ws.getCell(row, COL_NUM));
  ws.mergeCells(row, COL_LABEL, row, EMP_COL_END);
  const cell = ws.getCell(row, COL_LABEL);
  cell.value = title;
  cell.font = { ...BODY_FONT, bold: true };
  borderCell(cell);
  cell.alignment = { horizontal: "left", vertical: "middle" };
  return row + 1;
}

function addTurnoverRateTable(ws, row, financialYear, turnoverRate) {
  const fy = Number(financialYear) || new Date().getFullYear();
  const periods = [
    {
      label: `Turnover rate in current FY (${formatFyShort(fy)})`,
      node: turnoverRate?.currentfy,
    },
    {
      label: `Turnover rate in previous FY (${formatFyShort(fy - 1)})`,
      node: turnoverRate?.previousfy,
    },
    {
      label: `Turnover rate in the year prior to the previous FY (${formatFyShort(fy - 2)})`,
      node: turnoverRate?.priorfy,
    },
  ];

  const r1 = row;
  const r2 = row + 1;

  ws.mergeCells(r1, COL_LABEL, r2, COL_LABEL);
  setTableCell(ws, r1, COL_LABEL, "Particulars", { align: "left", skipBorder: true });
  applyWorkforceHeaderFill(ws, r1, COL_NUM, TURNOVER_TABLE_END);
  applyWorkforceHeaderFill(ws, r2, COL_NUM, TURNOVER_TABLE_END);

  periods.forEach((period, idx) => {
    const startCol = 6 + idx * TURNOVER_FY_COLS;
    const endCol = startCol + TURNOVER_FY_COLS - 1;
    ws.mergeCells(r1, startCol, r1, endCol);
    setTableCell(ws, r1, startCol, period.label, { skipBorder: true });
    ["Male", "Female", "Other", "Total"].forEach((sub, i) => {
      setTableCell(ws, r2, startCol + i, sub, { skipBorder: true });
    });
  });

  borderTurnoverHeaderRow1(ws, r1);
  borderTurnoverHeaderRow2(ws, r2);
  row = r2 + 1;

  [
    { label: "Permanent Employees", key: "employees" },
    { label: "Permanent Workers", key: "workers" },
  ].forEach(({ label, key }, idx) => {
    const isLast = idx === 1;
    setTableCell(ws, row, COL_LABEL, label, { align: "left", skipBorder: true });

    periods.forEach((period, periodIdx) => {
      const obj = period.node?.[key];
      const startCol = 6 + periodIdx * TURNOVER_FY_COLS;
      setTableCell(ws, row, startCol, formatPct(obj?.male), { align: "right", valueFill: true, skipBorder: true });
      setTableCell(ws, row, startCol + 1, formatPct(obj?.female), { align: "right", valueFill: true, skipBorder: true });
      setTableCell(ws, row, startCol + 2, getTurnoverOtherPct(obj), { align: "right", valueFill: true, skipBorder: true });
      setTableCell(ws, row, startCol + 3, formatPct(obj?.total), { align: "right", valueFill: true, skipBorder: true });
    });

    borderTurnoverDataRow(ws, row, { lastRow: isLast });
    row += 1;
  });

  return row;
}

function addEmployeesSection(ws, row, employeesAndWorkers, financialYear) {
  const fy = Number(financialYear) || new Date().getFullYear();

  styleSubSectionHeader(ws, row, "IV.", "Employees", "greyRoman");
  row += 1;

  row = addFullWidthLabelRow(
    ws,
    row,
    "Details as at the end of Financial Year",
    "25",
    EMP_COL_END,
    { sectionTitle: true }
  );
  row = addWorkforceSectionTitle(
    ws,
    row,
    "A.",
    "Employees and workers (including differently abled):"
  );
  row = addWorkforceTableHeader(ws, row);

  let sr = 1;
  ({ row, nextSr: sr } = addWorkforceGroup(ws, row, "EMPLOYEES", employeesAndWorkers?.emp, [
    { label: "Permanent (D)", key: "permanent" },
    { label: "Other than permanent (E)", key: "other" },
    { label: "Total employees(D + E)", key: "total" },
  ], sr));

  ({ row } = addWorkforceGroup(ws, row, "WORKERS", employeesAndWorkers?.worker, [
    { label: "Permanent (F)", key: "permanent" },
    { label: "Other than permanent (G)", key: "other" },
    { label: "Total workers (F + G)", key: "total" },
  ], sr));

  row = addWorkforceSectionTitle(
    ws,
    row,
    "B.",
    "Differently abled Employees and workers:"
  );
  row = addWorkforceTableHeader(ws, row);

  sr = 1;
  ({ row, nextSr: sr } = addWorkforceGroup(
    ws,
    row,
    "DIFFERENTLY ABLED EMPLOYEES",
    employeesAndWorkers?.differentlyabled?.employees,
    [
      { label: "Permanent (D)", key: "permanent" },
      { label: "Other than Permanent (E)", key: "other" },
      { label: "Total differently abled employees (D + E)", key: "total" },
    ],
    sr
  ));

  ({ row } = addWorkforceGroup(
    ws,
    row,
    "DIFFERENTLY ABLED WORKERS",
    employeesAndWorkers?.differentlyabled?.workers,
    [
      { label: "Permanent (F)", key: "permanent" },
      { label: "Other than Permanent (G)", key: "other" },
      { label: "Total differently abled workers (F + G)", key: "total" },
    ],
    sr
  ));

  row = addFullWidthLabelRow(
    ws,
    row,
    "Participation/Inclusion/Representation of women",
    "26",
    COL_VALUE_END,
    { sectionTitle: true }
  );
  row = addWomenRepTable(ws, row, employeesAndWorkers?.womenRepresentation);

  row = addFullWidthLabelRow(
    ws,
    row,
    "Turnover rate for permanent employees and workers (Disclose trends for the past 3 years)",
    "27",
    TURNOVER_TABLE_END,
    { extendedTitle: true }
  );
  row = addTurnoverRateTable(ws, row, fy, employeesAndWorkers?.turnoverRate);

  return row;
}

const COMPLAINTS_TABLE_END = 14;
const STAKEHOLDER_GROUPS = [
  ["communities", "Communities"],
  ["investors", "Investors (other than shareholders)"],
  ["shareHolders", "Shareholders"],
  ["employeesAndWorkers", "Employees and workers"],
  ["customers", "Customers"],
  ["valueChainParnter", "Value Chain Partners"],
  ["other", "Other (please specify)"],
];

function resolveGrievanceMechanism(weblink, explicitValue) {
  const explicit = safeText(explicitValue);
  if (explicit) return explicit;
  return safeText(weblink) ? "Yes" : "";
}

function borderComplaintsHeaderRow1(ws, row) {
  ws.getCell(row, COL_NUM).border = { left: MEDIUM };
  ws.getCell(row, COL_LABEL).border = { top: THIN, left: THIN, right: THIN };
  ws.getCell(row, 6).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 7).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 8).border = { top: THIN, left: THIN, bottom: THIN };
  ws.getCell(row, 9).border = { top: THIN, bottom: THIN };
  ws.getCell(row, 10).border = { top: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 11).border = { top: THIN, left: THIN, bottom: THIN };
  ws.getCell(row, 12).border = { top: THIN, bottom: THIN };
  ws.getCell(row, 13).border = { top: THIN, bottom: THIN };
  ws.getCell(row, 14).border = THIN_BLACK;
}

function borderComplaintsHeaderRow2(ws, row) {
  ws.getCell(row, COL_NUM).border = { left: MEDIUM };
  ws.getCell(row, COL_LABEL).border = { left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 6).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 7).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
  for (let c = 8; c <= 12; c += 1) {
    ws.getCell(row, c).border = THIN_BLACK;
  }
  ws.getCell(row, 13).border = { top: THIN, left: THIN, bottom: THIN };
  ws.getCell(row, 14).border = THIN_BLACK;
}

function borderComplaintsDataRow(ws, row, { first = false, last = false, otherRow = false } = {}) {
  if (last && otherRow) {
    ws.getCell(row, COL_NUM).border = { left: MEDIUM, right: THIN, bottom: THIN };
    ws.getCell(row, COL_LABEL).border = { left: THIN, bottom: THIN };
    ws.getCell(row, 6).border = { top: THIN, left: THIN, right: THIN };
    for (let c = 7; c <= COL_SECTION_END; c += 1) {
      ws.getCell(row, c).border = {};
    }
    return;
  }

  if (last) {
    ws.getCell(row, COL_NUM).border = { left: MEDIUM, right: THIN, bottom: THIN };
    ws.getCell(row, COL_LABEL).border = { left: THIN, bottom: THIN };
    ws.getCell(row, 6).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
    ws.getCell(row, 7).border = { top: THIN, right: THIN, bottom: THIN };
    ws.getCell(row, 8).border = { top: THIN, bottom: THIN };
    ws.getCell(row, 9).border = { top: THIN, bottom: THIN };
    ws.getCell(row, 10).border = { top: THIN, right: THIN, bottom: THIN };
    ws.getCell(row, 11).border = { top: THIN, bottom: THIN };
    ws.getCell(row, 12).border = { top: THIN, bottom: THIN };
    ws.getCell(row, 13).border = { top: THIN, right: THIN, bottom: THIN };
    ws.getCell(row, 14).border = { left: THIN, right: THIN, bottom: THIN };
    return;
  }

  const numBorder = first
    ? { top: THIN, left: MEDIUM, right: THIN, bottom: THIN }
    : { left: MEDIUM, right: THIN, bottom: THIN };
  ws.getCell(row, COL_NUM).border = numBorder;
  ws.getCell(row, COL_LABEL).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 6).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 7).border = { top: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 8).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 9).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 10).border = THIN_BLACK;
  ws.getCell(row, 11).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 12).border = { top: THIN, left: THIN, right: THIN, bottom: THIN };
  ws.getCell(row, 13).border = { top: THIN, left: THIN, bottom: THIN };
  ws.getCell(row, 14).border = THIN_BLACK;
}

function addComplaintsTable(ws, row, financialYear, currentFY, previousFY) {
  const fy = Number(financialYear) || new Date().getFullYear();
  const r1 = row;
  const r2 = row + 1;

  setTableCell(ws, r1, COL_LABEL, "Stakeholder group from whom complaint is received", {
    bold: true,
    align: "left",
    skipBorder: true,
  });
  setTableCell(ws, r1, 6, "Grievance Redressal Mechanism in Place (Yes/No/NA)", {
    bold: true,
    align: "left",
    skipBorder: true,
  });
  setTableCell(ws, r1, 7, "(If Yes, then provide web-link for grievance redress policy)", {
    bold: true,
    align: "left",
    skipBorder: true,
  });
  setTableCell(ws, r1, 8, `FY ${formatFyShort(fy)} (Current Financial Year)`, { bold: true, skipBorder: true });
  setTableCell(ws, r1, 11, `FY ${formatFyShort(fy - 1)} (Previous Financial Year)`, { bold: true, skipBorder: true });
  setTableCell(ws, r1, 14, "(If NA, then provide the reason)", { bold: true, skipBorder: true });

  [
    { col: 8, label: "Number of complaints filed during the year" },
    { col: 9, label: "Number of complaints pending resolution at close of the year" },
    { col: 10, label: "Remarks" },
    { col: 11, label: "Number of complaints filed during the year" },
    { col: 12, label: "Number of complaints pending resolution at close of the year" },
    { col: 13, label: "Remarks" },
    { col: 14, label: "(If NA, then provide the reason)" },
  ].forEach(({ col, label }) => {
    setTableCell(ws, r2, col, label, { bold: true, skipBorder: true });
  });

  borderComplaintsHeaderRow1(ws, r1);
  borderComplaintsHeaderRow2(ws, r2);
  applyRowFill(ws, r1, SUBSECTION_TITLE_FILL, COL_NUM, COMPLAINTS_TABLE_END);
  applyRowFill(ws, r2, SUBSECTION_TITLE_FILL, COL_NUM, COMPLAINTS_TABLE_END);
  applyTrailingBannerFill(ws, r1, COMPLAINTS_TABLE_END + 1);
  applyTrailingBannerFill(ws, r2, COMPLAINTS_TABLE_END + 1);

  ws.mergeCells(r1, COL_LABEL, r2, COL_LABEL);
  ws.mergeCells(r1, 6, r2, 6);
  ws.mergeCells(r1, 7, r2, 7);
  ws.mergeCells(r1, 8, r1, 10);
  ws.mergeCells(r1, 11, r1, 13);
  ws.mergeCells(r1, 14, r2, 14);
  row = r2 + 1;

  const groupCount = STAKEHOLDER_GROUPS.length;
  STAKEHOLDER_GROUPS.forEach(([key, label], idx) => {
    const current = currentFY?.[key] || {};
    const previous = previousFY?.[key] || {};
    const isOther = key === "other";

    setTableCell(ws, row, COL_LABEL, label, { align: "left", skipBorder: true });

    if (isOther) {
      const linkCell = ws.getCell(row, 6);
      linkCell.value = {
        text: "Add details",
        hyperlink: `#'${OTHER_STAKEHOLDER_GROUP_SHEET}'!D3`,
      };
      linkCell.font = LINK_FONT;
      linkCell.alignment = { horizontal: "center", vertical: "middle" };
      applyValueColumnFill(ws, row, 6);
    } else {
      const weblink = safeText(current?.grievanceRedressalWeblink);
      setTableCell(ws, row, 6, resolveGrievanceMechanism(weblink, current?.grievanceMechanism), {
        align: "center",
        valueFill: true,
        skipBorder: true,
      });
      setTableCell(ws, row, 7, weblink, { align: "left", valueFill: true, skipBorder: true });
      setTableCell(ws, row, 8, safeNum(current?.filed), { align: "right", valueFill: true, skipBorder: true });
      setTableCell(ws, row, 9, safeNum(current?.pending), { align: "right", valueFill: true, skipBorder: true });
      setTableCell(ws, row, 10, safeText(current?.remarks), { align: "left", valueFill: true, skipBorder: true });
      setTableCell(ws, row, 11, safeNum(previous?.filed), { align: "right", valueFill: true, skipBorder: true });
      setTableCell(ws, row, 12, safeNum(previous?.pending), { align: "right", valueFill: true, skipBorder: true });
      setTableCell(ws, row, 13, safeText(previous?.remarks), { align: "left", valueFill: true, skipBorder: true });
      setTableCell(ws, row, 14, safeText(current?.naReason || previous?.naReason), {
        align: "left",
        naFill: true,
        skipBorder: true,
      });
      applyTrailingBannerFill(ws, row, 15);
    }
    borderComplaintsDataRow(ws, row, {
      first: idx === 0,
      last: idx === groupCount - 1,
      otherRow: isOther,
    });
    if (isOther) applyTrailingBannerFill(ws, row, 7);
    row += 1;
  });

  return row;
}

function addRemainingDisclosuresSection(ws, row, financialYear, holding, transparency) {
  styleSubSectionHeader(
    ws,
    row,
    "V.",
    "Holding, Subsidiary and Associate Companies (including joint ventures)"
  );
  row += 1;

  row = addDetailLinkRow(
    ws,
    row,
    "28",
    "(a) Names of holding / subsidiary / associate companies / joint ventures",
    "'Holding,subsidiary or associate'",
    { sectionEnd: true }
  );

  styleSubSectionHeader(ws, row, "VI.", "CSR Details");
  row += 1;

  const csr = holding?.csrDetails || {};
  row = addSubPointRow(
    ws,
    row,
    "29",
    "(i) Whether CSR is applicable as per section 135 of Companies Act, 2013: (Yes/No)",
    csr?.applicable ? "Yes" : "No"
  );
  row = addSubPointRow(ws, row, "", "(ii) Turnover (in Rs.)", formatAmount(csr?.turnover), { valueBandEnd: true });
  row = addSubPointRow(ws, row, "", "(iii) Net worth (in Rs.)", formatAmount(csr?.netWorth), { sectionEnd: true });

  styleSubSectionHeader(ws, row, "VII.", "Transparency and Disclosures Compliances");
  row += 1;

  row = addFullWidthLabelRow(
    ws,
    row,
    "Complaints/Grievances on any of the principles (Principles 1 to 9) under the National Guidelines on Responsible Business Conduct:",
    "30",
    14,
    { font: { name: "Calibri", size: 12 }, bold: true, wideMerge: true }
  );
  row = addComplaintsTable(
    ws,
    row,
    financialYear,
    transparency?.currentFYComplaints,
    transparency?.previousFYComplaints
  );

  row = addDetailLinkRow(
    ws,
    row,
    "31",
    "Overview of the entity's material responsible business conduct issues",
    "'Business conduct issues'"
  );
  ws.getCell(row - 1, COL_LABEL).alignment = {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  };

  row = addLabelValueRow(ws, row, "32", "Notes", safeText(transparency?.notes), {
    withFill: false,
    mergeValueBand: false,
  });

  const notesRow = row - 1;
  ws.getCell(notesRow, COL_NUM).border = { top: THIN, left: MEDIUM, bottom: MEDIUM };
  ws.getCell(notesRow, COL_LABEL).border = { top: THIN, left: THIN, right: THIN, bottom: MEDIUM };
  ws.getCell(notesRow, COL_VALUE_START).border = {
    top: THIN,
    left: THIN,
    right: THIN,
    bottom: MEDIUM,
  };
  for (let c = COL_VALUE_START + 1; c <= COL_SECTION_END; c += 1) {
    const border = { bottom: MEDIUM };
    if (c === COL_SECTION_END) border.right = MEDIUM;
    ws.getCell(notesRow, c).border = border;
  }
  applyTrailingBannerFill(ws, notesRow);

  return row;
}

function setupGeneralDisclosuresSheet(ws) {
  const columnWidths = {
    A: 9.1796875,
    B: 9.1796875,
    C: 5.7265625,
    D: 7,
    E: 51.81640625,
    F: 20.453125,
    G: 20.453125,
    H: 20.453125,
    I: 20.453125,
    J: 20.453125,
    K: 20.453125,
    L: 20.453125,
    M: 20.453125,
    N: 20.453125,
    O: 20.453125,
    P: 20.453125,
    Q: 20.453125,
    R: 9.1796875,
    S: 9.1796875,
    T: 9.1796875,
  };
  Object.entries(columnWidths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });
  ws.getColumn("A").hidden = true;
  ws.getColumn("B").hidden = true;
  // Visible through T; hide U → XFD.
  for (let col = 21; col <= 16384; col += 1) {
    const column = ws.getColumn(col);
    column.width = 7.1796875;
    column.hidden = true;
  }
  ws.getRow(1).hidden = true;

  ws.views = [
    {
      showGridLines: false,
      state: "frozen",
      xSplit: 3,
      ySplit: FREEZE_Y_SPLIT,
      topLeftCell: "D6",
      zoomScale: 90,
    },
  ];
}

export function buildGenaralDisclosures(workbook, sectionA) {
  const ws = workbook.addWorksheet("General Disclosures");
  setupGeneralDisclosuresSheet(ws);
  ws.getRow(1).hidden = true;
  ws.getRow(1).height = 0;

  const li = sectionA?.listedIdentity || {};
  const ops = sectionA?.operations || {};
  const emp = sectionA?.employeesAndWorkers || {};
  const holding = sectionA?.holding || {};
  const transparency = sectionA?.transparencyAndDisclosures || {};
  const qc = li?.queryContact || {};
  const fyRanges = getFinancialYearRanges(li?.financialYear);
  const hasAssurance = Boolean(li?.assuranceProvider || li?.assuranceType);

  let row = SECTION_HEADER_ROW;
  styleSectionHeader(ws, row, "SECTION A: GENERAL DISCLOSURES");
  row += 1;

  styleSubSectionHeader(ws, row, "I.", "Details of the listed entity", "greyFull");
  row += 1;

  row = addLabelValueRow(ws, row, "1", "NSE Symbol", safeText(li?.nseSymbol), {
    mandatory: true,
    suppressNumTop: true,
  });
  row = addLabelValueRow(ws, row, "2", "BSE Scrip Code", safeText(li?.bseScripCode), {
    mandatory: true,
    suppressNumTop: true,
  });
  row = addLabelValueRow(ws, row, "3", "MSEI Symbol", safeText(li?.mseiSymbol), {
    mandatory: true,
    suppressNumTop: true,
  });
  row = addLabelValueRow(ws, row, "4", "ISIN", safeText(li?.isin), {
    mandatory: true,
    suppressNumTop: true,
  });

  row = addLabelValueRow(
    ws,
    row,
    "5",
    "Corporate Identity Number (CIN) of the Listed Entity",
    safeText(li?.corporateIdentityNumber),
    { suppressNumTop: true }
  );
  row = addLabelValueRow(ws, row, "6", "Name of the Listed Entity", safeText(li?.listedCompanyName));
  row = addLabelValueRow(
    ws,
    row,
    "7",
    "Date of Incorporation",
    li?.yearOfIncorporation ? formatIndianDate(1, 1, li.yearOfIncorporation) : ""
  );
  row = addLabelValueRow(
    ws,
    row,
    "8",
    "Registered office address",
    safeText(li?.registeredOfficeAddress)
  );
  row = addLabelValueRow(ws, row, "9", "Corporate address", safeText(li?.corporateOfficeAddress));
  row = addLabelValueRow(ws, row, "10", "E-mail", safeText(li?.companyEmail));
  row = addLabelValueRow(ws, row, "11", "Telephone", safeText(li?.companyTelephoneNumber));
  row = addLabelValueRow(ws, row, "12", "Website", safeText(li?.companyWebsite));

  row = addFinancialYearBlock(ws, row, fyRanges);

  row = addDetailLinkRow(
    ws,
    row,
    "14",
    "Name of the Stock Exchange(s) where shares are listed",
    "'Stock exchange'",
    { linkMergeEnd: COL_VALUE_END }
  );
  row = addLabelValueRow(
    ws,
    row,
    "15",
    "Paid-up Capital (In Rs)",
    formatAmount(li?.paidUpCapital)
  );

  row = addContactHeaderRow(
    ws,
    row,
    "16",
    "Name and contact details (telephone, email address) of the person who may be contacted in case of any queries on the BRSR report"
  );
  row = addSubLabelValueRow(ws, row, "Name", safeText(qc?.name));
  row = addSubLabelValueRow(ws, row, "Contact", safeText(qc?.telephone));
  row = addSubLabelValueRow(ws, row, "E mail", safeText(qc?.email));

  row = addLabelValueRow(
    ws,
    row,
    "17",
    "Reporting boundary - Are the disclosures under this report made on a standalone basis (i.e. only for the entity) or on a consolidated basis (i.e. for the entity and all the entities which form a part of its consolidated financial statements, taken together).",
    safeText(li?.reportingBoundary, "Standalone basis")
  );
  row = addLabelValueRow(
    ws,
    row,
    "18",
    "Whether the company has undertaken assessment or assurance of the BRSR Core?",
    hasAssurance ? "Yes" : "No",
    { labelMergeEnd: COL_H, mergeValueBand: false }
  );
  row = addLabelValueRow(
    ws,
    row,
    "19",
    "Name of assessment or assurance provider",
    safeText(li?.assuranceProvider),
    { labelMergeEnd: COL_H, mergeValueBand: false }
  );
  row = addLabelValueRow(
    ws,
    row,
    "20",
    "Type of assessment or assurance obtained",
    safeText(li?.assuranceType),
    { labelMergeEnd: COL_H, mergeValueBand: false, sectionEnd: true }
  );

  styleSubSectionHeader(ws, row, "II.", "Products/services");
  row += 1;

  row = addDetailLinkRow(
    ws,
    row,
    "21",
    "Details of business activities (accounting for 90% of the turnover)",
    "'Details of business activities'",
    { labelMergeEnd: COL_H }
  );
  row = addDetailLinkRow(
    ws,
    row,
    "22",
    "Products/Services sold by the entity (accounting for 90% of the entity's Turnover)",
    "'Products services sold'",
    { labelMergeEnd: COL_H, sectionEnd: true }
  );

  styleSubSectionHeader(ws, row, "III.", "Operations");
  row += 1;

  row = addFullWidthLabelRow(
    ws,
    row,
    "Number of locations where plants and/or operations/offices of the entity are situated",
    "23",
    COL_H
  );

  borderLocationHeaderRow(ws, row);
  ws.getCell(row, COL_LABEL).value = "Location";
  ws.getCell(row, COL_LABEL).font = { ...BODY_FONT, bold: true };
  ws.getCell(row, COL_LABEL).alignment = { horizontal: "left", vertical: "middle" };

  ws.getCell(row, COL_LOC_PLANTS).value = "Number of plants";
  ws.getCell(row, COL_LOC_PLANTS).font = { ...BODY_FONT, bold: true };
  ws.getCell(row, COL_LOC_PLANTS).alignment = { horizontal: "center", vertical: "middle" };

  ws.getCell(row, COL_LOC_OFFICES).value = "Number of offices";
  ws.getCell(row, COL_LOC_OFFICES).font = { ...BODY_FONT, bold: true };
  ws.getCell(row, COL_LOC_OFFICES).alignment = { horizontal: "center", vertical: "middle" };

  ws.getCell(row, COL_LOC_TOTAL).value = "Total";
  ws.getCell(row, COL_LOC_TOTAL).font = { ...BODY_FONT, bold: true };
  ws.getCell(row, COL_LOC_TOTAL).alignment = { horizontal: "center", vertical: "middle" };
  applyTrailingBannerFill(ws, row, COL_LOC_TOTAL + 1);
  row += 1;

  const locationRows = [
    {
      label: "National",
      plants: safeText(ops?.numberOfLocations?.national?.plants, "0"),
      offices: safeText(ops?.numberOfLocations?.national?.offices, "0"),
      total: safeText(ops?.numberOfLocations?.national?.total, "0"),
    },
    {
      label: "International",
      plants: safeText(ops?.numberOfLocations?.international?.plants, "0"),
      offices: safeText(ops?.numberOfLocations?.international?.offices, "0"),
      total: safeText(ops?.numberOfLocations?.international?.total, "0"),
    },
  ];

  locationRows.forEach((item, idx) => {
    borderLocationDataRow(ws, row, { includeLabelTop: idx === 0 });
    const labelCell = ws.getCell(row, COL_LABEL);
    labelCell.value = item.label;
    labelCell.font = BODY_FONT;
    labelCell.alignment = { horizontal: "left", vertical: "middle" };
    ws.getCell(row, COL_LOC_PLANTS).value = item.plants;
    ws.getCell(row, COL_LOC_OFFICES).value = item.offices;
    ws.getCell(row, COL_LOC_TOTAL).value = item.total;
    [COL_LOC_PLANTS, COL_LOC_OFFICES, COL_LOC_TOTAL].forEach((col) => {
      const cell = ws.getCell(row, col);
      cell.font = BODY_FONT;
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.fill = col === COL_LOC_TOTAL ? GREY_FILL : VALUE_FILL;
    });
    applyTrailingBannerFill(ws, row, COL_LOC_TOTAL + 1);
    row += 1;
  });

  const marketsNumCell = ws.getCell(row, COL_NUM);
  marketsNumCell.value = "24";
  marketsNumCell.font = BODY_FONT;
  borderMarketsTitleRow(ws, row);
  const marketsTitle = ws.getCell(row, COL_LABEL);
  marketsTitle.value = "Markets served by the entity";
  marketsTitle.font = BODY_FONT;
  marketsTitle.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
  applyTrailingBannerFill(ws, row, COL_LABEL + 2);
  row += 1;

  const marketsACell = ws.getCell(row, COL_NUM);
  marketsACell.value = "A";
  marketsACell.font = BODY_FONT;
  marketsACell.alignment = { horizontal: "right", vertical: "middle" };
  borderMarketsTitleRow(ws, row);
  const marketsALabel = ws.getCell(row, COL_LABEL);
  marketsALabel.value = "Number of locations";
  marketsALabel.font = { ...BODY_FONT, bold: true };
  marketsALabel.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
  applyTrailingBannerFill(ws, row, COL_LABEL + 2);
  row += 1;

  borderMarketsSubHeaderRow(ws, row);
  ws.getCell(row, COL_LABEL).value = "Locations";
  ws.getCell(row, COL_LABEL).font = { ...BODY_FONT, bold: true };
  ws.getCell(row, COL_LABEL).alignment = { horizontal: "left", vertical: "middle" };
  ws.getCell(row, COL_VALUE_START).value = "Number";
  ws.getCell(row, COL_VALUE_START).font = { ...BODY_FONT, bold: true };
  ws.getCell(row, COL_VALUE_START).alignment = { horizontal: "center", vertical: "middle" };
  applyTrailingBannerFill(ws, row, COL_VALUE_START + 1);
  row += 1;

  const marketRows = [
    { label: "National (No. of States)", count: safeText(ops?.marketsServed?.nationalStates, "0") },
    {
      label: "International (No. of Countries)",
      count: safeText(ops?.marketsServed?.internationalCountries, "0"),
    },
  ];

  marketRows.forEach((item) => {
    borderDataNumCell(ws.getCell(row, COL_NUM), { top: true });
    setLabelCell(ws, row, item.label);
    setValueCell(ws, row, item.count, { align: "center", mergeEnd: COL_VALUE_START });
    applyTrailingBannerFill(ws, row);
    row += 1;
  });

  const marketsBCell = ws.getCell(row, COL_NUM);
  marketsBCell.value = "B";
  marketsBCell.font = BODY_FONT;
  borderDataNumCell(marketsBCell, { top: true });
  marketsBCell.alignment = { horizontal: "right", vertical: "middle" };
  setLabelCell(
    ws,
    row,
    "What is the contribution of exports as a percentage of the total turnover of the entity?"
  );
  setValueCell(ws, row, safeText(ops?.marketsServed?.exportsContribution), {
    mergeEnd: COL_VALUE_START,
    align: "center",
  });
  applyTrailingBannerFill(ws, row);
  row += 1;

  const marketsCCell = ws.getCell(row, COL_NUM);
  marketsCCell.value = "C";
  marketsCCell.font = BODY_FONT;
  borderDataNumCell(marketsCCell, { top: true });
  marketsCCell.alignment = { horizontal: "right", vertical: "middle" };
  setLabelCell(ws, row, "A brief on types of customers");
  setValueCell(ws, row, safeText(ops?.marketsServed?.customerTypes), {
    mergeEnd: COL_VALUE_START,
    align: "left",
  });
  ws.getCell(row, COL_NUM).border = { top: THIN, left: MEDIUM, right: THIN, bottom: MEDIUM };
  ws.getCell(row, COL_LABEL).border = { bottom: MEDIUM };
  ws.getCell(row, COL_VALUE_START).border = {
    top: THIN,
    left: THIN,
    right: THIN,
    bottom: MEDIUM,
  };
  applyMediumBottomTail(ws, row, COL_VALUE_START + 1, COL_SECTION_END);
  applyTrailingBannerFill(ws, row);
  row += 1;

  row = addEmployeesSection(ws, row, emp, li?.financialYear);
  row = addRemainingDisclosuresSection(ws, row, li?.financialYear, holding, transparency);

  applySheetOuterBox(ws, SECTION_HEADER_ROW, row - 1);
  restoreSectionHeaderBorders(ws, SECTION_HEADER_ROW);
  ws.getRow(1).hidden = true;

  return ws;
}
